/**
 * useMfaEnrollConfirm — admin + tenant variants for
 * ``POST .../mfa/enroll/confirm``.
 *
 * Sends the user's first 6-digit TOTP code to flip the credential to
 * ``active``. On success the backend mints an access JWT + sets the
 * refresh cookie; the response shape matches the legacy login envelope.
 */
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";

export interface MfaEnrollConfirmResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
}

interface ConfirmInput {
  code: string;
}

function authHeader(): { Authorization: string } {
  const token = useAuthStore.getState().mfaPendingToken;
  if (!token) {
    throw new Error("mfaPendingToken missing from store");
  }
  return { Authorization: `Bearer ${token}` };
}

export function useAdminMfaEnrollConfirm() {
  return useMutation<MfaEnrollConfirmResponse, ApiError, ConfirmInput>({
    mutationFn: async ({ code }) => {
      const resp = await apiClient.post<MfaEnrollConfirmResponse>(
        "/admin/mfa/enroll/confirm",
        { code },
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}

export function useTenantMfaEnrollConfirm(tenantSlug: string) {
  return useMutation<MfaEnrollConfirmResponse, ApiError, ConfirmInput>({
    mutationFn: async ({ code }) => {
      const resp = await apiClient.post<MfaEnrollConfirmResponse>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/mfa/enroll/confirm`,
        { code },
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}
